import endpoints from "constants/api/Endpoints";
import UpdatePayment from "interface/UpdatePayment";
import { logError } from "services/logService/errorLogger";
import { LowLevelPaymentMethod } from "interface/LowLevelPaymentMethod";
import { DefaultApiServiceReturnType } from "constants/api/models/response/DefaultApiResponse";
import { LegacyPaymentProps } from "interface/LegacyPaymentProps";

const tag = 'PaymentApiService'

/**
 * Updates subscription payment status.
 *
 * @async
 * @param {UpdatePayment} params Includes the payment Id and the status of the transaction.
 * @returns {Promise<boolean>} 
 */
export const processSubscriptionResponse = async (params: UpdatePayment): Promise<boolean> => {
    try {
        if (!params) {
            logError('Missing payment payload.', params, tag)
            return false
        }
        
        const result = await endpoints.updateSubscriptionPayment({ tag: tag, data: params })
        return result
    } catch (error) {
        logError(
            'Error updating payment response',
            { error, params },
            tag
        )
        return false
    }
}

/**
 * Fetches the default payment method.
 *
 * @async
 * @param {string} tag The tag for the endpoint.
 * @returns {Promise<LowLevelPaymentMethod | null>} A promise that resolves to the default payment method or null.
 * @throws Logs an error if the fetch operation fails.
 * 
 * @example
 * const paymentMethod = await fetchDefaultPaymentMethod('PaymentMethod');
 * console.log('Payment method:', paymentMethod);
 */
export async function fetchDefaultPaymentMethod(tag: string): Promise<LowLevelPaymentMethod | null> {

    try {
        const result = await endpoints.fetchDefaultPaymentMethod({ tag }) as DefaultApiServiceReturnType<LowLevelPaymentMethod>;
        if (result && result.success && result.data) {
            return result.data ? result.data : null
        }
        logError('Failed to fetch default payment method', { error: result.error }, tag)
        return null
    } catch (error) {
        logError('Error fetching default payment method', { error }, tag)
        return null
    }
}

/** Fetches all card payment methods.
 * 
 * @async
 * 
 * @param {string} tag The tag for the endpoint.
 * @return {Promise<LowLevelPaymentMethod[] | null>} A promise that resolves to an array of card payment methods or null.
 * 
 * @throws Logs an error if the fetch operation fails.
 * 
 * @example
 * const cardMethods = await fetchAllCardPaymentMethods('CardMethods');
 * console.log('Card payment methods:', cardMethods);
 * */
export async function fetchAllCardPaymentMethods(tag: string): Promise<LowLevelPaymentMethod[] | null> {
    try {
        const result = await endpoints.fetchAllCardPaymentMethods({ tag })

        return result && result.success && result.data ? result.data : []
    } catch (error) {
        logError('Error fetching all card payment methods', { error }, tag)
        return null
    }
}

interface ProcessLegacySubscriptionPaymentResponseParams {
    tag: string;
    data: LegacyPaymentProps;
}

/**
 * Processes a legacy subscription payment response.
 * @param {ProcessLegacySubscriptionPaymentResponseParams} params - The parameters for processing the payment response.
 * @param {string} params.tag - The tag for logging purposes.
 * @param {Object} params.data - The payment data.
 * @param {string} [params.data.paymentId] - The ID of the payment (optional).
 * @param {'succeeded' | 'failed' | 'canceled'} params.data.status - The status of the payment.
 * @param {'subscription_upgrade' | 'one_time'} params.data.type - The type of payment.
 * @returns {Promise<boolean>} A promise that resolves to true if the processing was successful, otherwise false.
 */
export async function processLegacySubscriptionPaymentResponse({ tag, data }: ProcessLegacySubscriptionPaymentResponseParams): Promise<boolean> {
    try {
        if (!data) {
            logError('Missing payment payload.', data, tag)
            return false
        }

        const result = await endpoints.processLegacySubscriptionPayment({ tag, data })
        return result.success
    } catch (error) {
        logError(
            'Error processing legacy subscription payment',
            { error, data },
            tag
        )
        return false
    }
}

/**
 * Detaches a payment method.
 * @param {Object} params - The parameters for detaching the payment method.
 * @param {string} params.tag - The tag associated with the endpoint.
 * @param {string} params.paymentMethodId - The ID of the payment method to be detached.
 * @returns {Promise<boolean>} A promise that resolves to true if the detachment was successful, otherwise false.
 */
export async function detachPaymentMethod({ tag, paymentMethodId }: { tag: string; paymentMethodId: string }): Promise<boolean> {
    try {
        const result = await endpoints.detachPaymentMethod({ tag, data: { paymentMethodId } })
        return result.success
    } catch (error) {
        logError(
            'Error detaching payment method',
            { error, paymentMethodId },
            tag
        )
        return false
    }
}

/** Sets a payment method as the default.
 * @param {Object} params - The parameters for setting the default payment method.
 * @param {string} params.tag - The tag associated with the endpoint.
 * @param {string} params.paymentMethodId - The ID of the payment method to be set as default.
 * 
 * @returns {Promise<boolean>} A promise that resolves to true if the operation was successful, otherwise false.
 */
export async function setDefaultPaymentMethod({ tag, paymentMethodId }: { tag: string; paymentMethodId: string }): Promise<boolean> {
    try {
        const result = await endpoints.setDefaultPaymentMethod({ tag, data: { paymentMethodId } })
        return result.success
    } catch (error) {
        logError(
            'Error setting default payment method',
            { error, paymentMethodId },
            tag
        )
        return false
    }
}

/** Initializes a new payment method setup and retrieves the client secret.
 * 
 * @param {string} tag - The tag for the endpoint.
 * @returns {Promise<string | null>} The client secret if successful, otherwise null.
 */
export async function initializeNewPaymentMethodSetup(tag: string): Promise<string | null> {
    try {
        const result = await endpoints.newPaymentMethodSetup({ tag })
        return result && result.success && result.clientSecret ? result.clientSecret : null
    } catch (error) {
        logError(
            'Error initializing new payment method setup',
            { error },
            tag
        )
        return null
    }
}